import { legacyApi } from '../../../services/legacyApi';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { RequiredLabel } from '../../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../../components/forms/formValidationUtils';

export const CreateInquiry: React.FC = () => {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [customers, setCustomers] = useState<any[]>([]);
    const [projectTypes, setProjectTypes] = useState<any[]>([]);

    // Form State
    const [customerId, setCustomerId] = useState('');
    const [projectTypeId, setProjectTypeId] = useState('1'); // Default GRC
    const [scope, setScope] = useState('');
    const [segment, setSegment] = useState('');
    const [desc, setDesc] = useState('');
    const [attention, setAttention] = useState('');
    const [enquiryRef, setEnquiryRef] = useState('Mail Ref/Whatsapp Ref');
    const [customerAddress, setCustomerAddress] = useState('');
    const [followup, setFollowup] = useState(new Date().toISOString().split('T')[0]);
    const [remarks, setRemarks] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    React.useEffect(() => {
        legacyApi.customers.list().then(setCustomers).catch(console.error);
        legacyApi.getProjectTypes().then(setProjectTypes).catch(console.error);
    }, []);

    const handleCustomerChange = (value: string) => {
        setCustomerId(value);
        const selectedCustomer = customers.find((c: any) => String(c.customer_id) === value);
        setAttention(selectedCustomer?.poc_name || selectedCustomer?.poc_contact || '');
        setCustomerAddress(selectedCustomer?.address || '');
        if (!enquiryRef) {
            setEnquiryRef('Mail Ref/Whatsapp Ref');
        }
    };

    const handleSave = async () => {
        const validation = validateRequiredFields([
            { key: 'customer_id', label: 'Customer', value: customerId, required: true },
            { key: 'project_type_id', label: 'Project Type', value: projectTypeId, required: true },
            { key: 'project_scope', label: 'Project Scope', value: scope, required: true },
        ]);
        if (!validation.valid) {
            setFieldErrors(validation.errorsByField);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField);
            return;
        }
        setFieldErrors({});

        setBusy(true);
        try {
            await legacyApi.inquiries.create({
                customer_id: parseInt(customerId),
                project_type_id: parseInt(projectTypeId),
                project_scope: scope,
                project_description: desc,
                attention: attention || undefined,
                enquiry_ref: enquiryRef || undefined,
                customer_address: customerAddress || undefined,
                inquiry_status_id: 1,
                followup_date: followup,
                remarks: remarks,
                segment: segment
            });
            toast.success('Inquiry created successfully!');
            navigate('/legacy/inquiries');
        } catch (e: any) {
            console.error(e);
            toast.error('Failed to create inquiry: ' + e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6 max-w-5xl mx-auto pb-12">
            {/* Header */}
            <div className="flex justify-between items-end">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/legacy/inquiries')} className="-ml-2">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight text-foreground">New Inquiry</h1>
                        <p className="text-muted-foreground">Create a new customer inquiry record</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate('/legacy/inquiries')}>Cancel</Button>
                    <Button onClick={handleSave} disabled={busy}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Inquiry
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    {/* Key Details Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Project Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <RequiredLabel htmlFor="customer_id" required>Customer</RequiredLabel>
                                    <select
                                        id="customer_id"
                                        name="customer_id"
                                        aria-invalid={!!fieldErrors.customer_id}
                                        aria-required="true"
                                        className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 ${fieldErrors.customer_id ? 'border-red-500 focus-visible:ring-red-100' : 'border-input focus-visible:ring-ring'}`}
                                        value={customerId}
                                        onChange={(e) => handleCustomerChange(e.target.value)}
                                    >
                                        <option value="">Select Customer...</option>
                                        {customers.map(c => (
                                            <option key={c.customer_id} value={c.customer_id}>{c.customer_name}</option>
                                        ))}
                                    </select>
                                    <FieldErrorText id="customer_id_error" message={fieldErrors.customer_id} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-medium">Segment</label>
                                    <select
                                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                        value={segment}
                                        onChange={(e) => setSegment(e.target.value)}
                                    >
                                        <option value="">Select Segment...</option>
                                        <option value="Marine">Marine</option>
                                        <option value="Planter">Planter</option>
                                        <option value="Projects">Projects</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <RequiredLabel htmlFor="project_type_id" required>Project Type</RequiredLabel>
                                    <select
                                        id="project_type_id"
                                        name="project_type_id"
                                        aria-invalid={!!fieldErrors.project_type_id}
                                        aria-required="true"
                                        className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 ${fieldErrors.project_type_id ? 'border-red-500 focus-visible:ring-red-100' : 'border-input focus-visible:ring-ring'}`}
                                        value={projectTypeId}
                                        onChange={(e) => setProjectTypeId(e.target.value)}
                                    >
                                        <option value="">Select Type...</option>
                                        {projectTypes.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                    <FieldErrorText id="project_type_id_error" message={fieldErrors.project_type_id} />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-sm font-medium">Attention</label>
                                    <Input
                                        value={attention}
                                        onChange={(e: any) => setAttention(e.target.value)}
                                        placeholder="POC name"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-medium">Enquiry Ref</label>
                                    <Input
                                        value={enquiryRef}
                                        onChange={(e: any) => setEnquiryRef(e.target.value)}
                                        placeholder="Mail Ref/Whatsapp Ref"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-medium">Customer Detailed Address</label>
                                <textarea
                                    className="flex min-h-[90px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                    value={customerAddress}
                                    onChange={(e) => setCustomerAddress(e.target.value)}
                                    placeholder="Customer address for quotation..."
                                />
                            </div>
                            <div className="space-y-1">
                                <RequiredLabel htmlFor="project_scope" required>Project Scope</RequiredLabel>
                                <Input
                                    id="project_scope"
                                    name="project_scope"
                                    type="text"
                                    aria-invalid={!!fieldErrors.project_scope}
                                    aria-required="true"
                                    value={scope}
                                    onChange={(e: any) => setScope(e.target.value)}
                                    placeholder="e.g. Turnkey GRC Cladding"
                                />
                                <FieldErrorText id="project_scope_error" message={fieldErrors.project_scope} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-medium">Project Description</label>
                                <textarea
                                    className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                    value={desc}
                                    onChange={(e) => setDesc(e.target.value)}
                                    placeholder="Describe the project requirements..."
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Remarks</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <textarea
                                className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Any additional internal remarks..."
                            />
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    {/* Status & Follow-up */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Status & Follow-up</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-sm font-medium">Inquiry Status</label>
                                <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                                    New (set automatically)
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-medium">Follow-up Date</label>
                                <Input
                                    type="date"
                                    value={followup}
                                    onChange={(e: any) => setFollowup(e.target.value)}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <div className="p-4 bg-muted/30 rounded-lg border border-dashed text-center">
                        <p className="text-sm text-muted-foreground">Once saved, you can convert this inquiry to a formal estimate.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};
